import React from 'react';
import { vars } from '../../theme';
import HBText from '../HBText/HBText';
import HBButton from '../HBButton/HBButton';
import HBLinkButton from '../HBLinkButton/HBLinkButton';
import HBLink from '../HBLink/HBLink';
import {
    ActionsContainer,
    ContentContainer,
    EmptyStateContainer,
    GraphicWrapper,
    TextContainer
} from './HBEmptyState.styles';
import type { HBEmptyStateProps } from './HBEmptyState.types';

const HBEmptyState = ({
    size = 'sm',
    title,
    message,
    graphic,
    primaryAction,
    secondaryAction,
    customPrimaryAction,
    customSecondaryAction,
    actionsLayout,
    helpLink,
    className,
    testId
}: HBEmptyStateProps) => {
    const hasPrimaryAction = primaryAction || customPrimaryAction;
    const hasSecondaryAction = secondaryAction || customSecondaryAction;
    const isSmallSize = size === 'sm';
    const hasActions = hasPrimaryAction || hasSecondaryAction;

    // Determine if actions should stack based on layout prop or existing logic
    const shouldStackActions =
        actionsLayout === 'vertical' ||
        (!actionsLayout &&
            primaryAction?.variant === 'button' &&
            !!hasSecondaryAction);

    return (
        <EmptyStateContainer
            $size={size}
            className={className}
            data-testid={testId}
        >
            {graphic && <GraphicWrapper $size={size}>{graphic}</GraphicWrapper>}

            <ContentContainer>
                <TextContainer $size={size}>
                    {title && (
                        <HBText
                            type={isSmallSize ? 'title16' : 'title18'}
                            color="text.primary"
                            align="center"
                        >
                            {title}
                        </HBText>
                    )}
                    {message && (
                        <HBText
                            type={isSmallSize ? 'body14' : 'body16'}
                            color="text.secondary"
                            align="center"
                        >
                            {message}
                        </HBText>
                    )}
                </TextContainer>

                {hasActions && (
                    <ActionsContainer $isStacked={shouldStackActions}>
                        {customPrimaryAction
                            ? customPrimaryAction
                            : primaryAction &&
                              (primaryAction.variant === 'button' ? (
                                  <HBButton
                                      {...primaryAction}
                                      testIdName={primaryAction.testIdName}
                                      size={size}
                                      buttonTheme={
                                          primaryAction.buttonTheme ??
                                          'secondary-solid-primary'
                                      }
                                  />
                              ) : (
                                  <HBLinkButton
                                      {...primaryAction}
                                      testIdName={primaryAction.testIdName}
                                      size={size}
                                  />
                              ))}
                        {customSecondaryAction
                            ? customSecondaryAction
                            : secondaryAction && (
                                  <HBLinkButton
                                      testIdName={secondaryAction.testIdName}
                                      size={size}
                                      {...secondaryAction}
                                  />
                              )}
                    </ActionsContainer>
                )}

                {helpLink && (
                    <HBLink
                        textType="body12"
                        color={vars.colors.text.secondary}
                        href={helpLink.href}
                        onClick={helpLink.onClick}
                        role={helpLink.href ? 'link' : 'button'}
                        target={helpLink.target}
                        testIdName="empty-state-help-link"
                    >
                        {helpLink.text}
                    </HBLink>
                )}
            </ContentContainer>
        </EmptyStateContainer>
    );
};

export default HBEmptyState;
